'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { type ComponentType, startTransition, useEffect, useId, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/app-tooltip';
import { Settings02IconSolid } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { logger } from '@/lib/telemetry/logger';
import { DURATION_EXIT_MS } from '@/lib/ui/easing';
import { ChartSettingsPanel } from './chart-settings-panel';
const ChartHelpModal: ComponentType<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = dynamic(() => import('./chart-help-modal').then(module => ({
  default: module.ChartHelpModal
})),
// Without `loading`, app-dynamic renders the lazy bare (no local Suspense
// boundary), so the one-off first-mount suspension escapes to the ROUTE
// boundary and repaints the whole market page as its loading skeleton.
{
  loading: () => null
});

/**
 * MOR-1808: warm the guide chunk as soon as settings opens. The first
 * "Learn more" click used to pay the dynamic-import download mid handoff,
 * landing the guide mount well after the settings sheet's exit animation and
 * visibly re-rendering the transition. With the module already resolved, the
 * first click behaves like every later one. (Module-level so the React
 * Compiler doesn't see the dynamic import inside the component.)
 */
function preloadChartGuide(): void {
  void import('./chart-help-modal');
}
export function ChartSettingsMenu() {
  const $ = _c(38);
  const t = useTranslations("chart");
  const settingsTriggerId = useId();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideMounted, setGuideMounted] = useState(false);
  const settingsOpenRef = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      id: null
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const transitionTimerRef = useRef(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const transitionTimer = transitionTimerRef.current;
      return () => {
        if (transitionTimer.id !== null) {
          window.clearTimeout(transitionTimer.id);
        }
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function updateSettingsOpen(open) {
      settingsOpenRef.current = open;
      setSettingsOpen(open);
      if (open) {
        preloadChartGuide();
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const updateSettingsOpen = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function scheduleAfterOverlayExit(callback) {
      if (transitionTimerRef.current.id !== null) {
        window.clearTimeout(transitionTimerRef.current.id);
      }
      transitionTimerRef.current.id = window.setTimeout(() => {
        transitionTimerRef.current.id = null;
        callback();
      }, DURATION_EXIT_MS);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const scheduleAfterOverlayExit = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function openGuideFromSettings() {
      updateSettingsOpen(false);
      scheduleAfterOverlayExit(() => {
        if (settingsOpenRef.current) {
          logger.warn("chart.guide.handoff_aborted", {
            reason: "settings_reopened"
          });
          return;
        }
        startTransition(() => {
          setGuideMounted(true);
          setGuideOpen(true);
        });
      });
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const openGuideFromSettings = t5;
  let t6;
  if ($[6] !== settingsTriggerId) {
    t6 = function updateGuideOpen(open_0) {
      setGuideOpen(open_0);
      if (!open_0) {
        scheduleAfterOverlayExit(() => {
          document.getElementById(settingsTriggerId)?.focus();
        });
      }
    };
    $[6] = settingsTriggerId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const updateGuideOpen = t6;
  let t7;
  if ($[8] !== t) {
    t7 = t("settings_title");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => updateSettingsOpen(true);
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== settingsTriggerId || $[12] !== t7) {
    t9 = <TooltipTrigger asChild={true}><AnimatedButton id={settingsTriggerId} type="button" variant="tertiary" size="xs" prefix={Settings02IconSolid} aria-label={t7} onClick={t8} /></TooltipTrigger>;
    $[11] = settingsTriggerId;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t("settings_title");
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <TooltipContent side="top"><p className="text-caption">{t10}</p></TooltipContent>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t9) {
    t12 = <Tooltip>{t9}{t11}</Tooltip>;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t) {
    t13 = t("settings_title");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t) {
    t14 = t("settings_description");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ChartSettingsPanel onOpenChartGuide={openGuideFromSettings} />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== settingsOpen || $[27] !== t13 || $[28] !== t14) {
    t16 = <SilkSheet open={settingsOpen} onOpenChange={updateSettingsOpen} variant="detached" size="sm" title={t13} description={t14}>{t15}</SilkSheet>;
    $[26] = settingsOpen;
    $[27] = t13;
    $[28] = t14;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== guideMounted || $[31] !== guideOpen || $[32] !== updateGuideOpen) {
    t17 = guideMounted ? <ChartHelpModal open={guideOpen} onOpenChange={updateGuideOpen} /> : null;
    $[30] = guideMounted;
    $[31] = guideOpen;
    $[32] = updateGuideOpen;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t12 || $[35] !== t16 || $[36] !== t17) {
    t18 = <>{t12}{t16}{t17}</>;
    $[34] = t12;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}